import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Avatar } from '@mui/material';
import {
    Video,
    FileText,
    Monitor,
    Phone,
    ChevronRight,
    ExternalLink,
    CreditCard,
} from 'lucide-react';
import { setUploadGuidelineVideoDialogOpen } from '../../../redux/reducers/loginSlice';
import UploadGuidelineVideoModel from '../../UploadGuidelineVideoModel/UploadGuidelineVideoModel';
import { fetchMySubscriptions, fetchSubscriptionUsage, getSubscriptionUsage } from '../../../redux/network/userApi';

const guidelines = [
    {
        id: "1",
        guideline: "Position camera 2.5m behind baseline (or as far back as possible). Use tripod at 1.5-2m height.",
    },
    {
        id: "2",
        guideline: "Use bright, even lighting. Keep court clear of obstructions.",
    },
    {
        id: "3",
        guideline: "Format: MP4 only. Resolution: 1080p at 30fps. Max size: 2GB.",
    },
    {
        id: "4",
        guideline: "For best results use Chrome (Android) or Safari (iPhone). Submit via Visist.ai app.",
    },
];

const getNumericValue = (...values) => {
    for (const value of values) {
        const numeric = Number(value);
        if (Number.isFinite(numeric)) return numeric;
    }
    return null;
};

const getRemainingQuota = (limit, used) => {
    const max = getNumericValue(limit);
    if (max === null) return null;
    const consumed = getNumericValue(used) ?? 0;
    return Math.max(max - consumed, 0);
};

const formatCountValue = (value, suffix = '') => {
    if (value === null || value === undefined) return '--';
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return '--';
    const display = Number.isInteger(numeric) ? numeric.toString() : numeric.toFixed(1);
    return suffix ? `${display} ${suffix}` : display;
};

const GamePostSidebar = ({ isMenuOpen, toggleMenu }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const isGameVideoPath = pathname.includes("game-videos");
    const [isCollapsed, setIsCollapsed] = useState(false);
    const userData = useSelector((state) => state.user.profile);
    const membershipProfile = userData?.value?.profileData?.membershipProfile;

    // Active plan state from Redux
    const subscriptionsState = useSelector((state) => state.user.subscriptions);
    const jwtToken = localStorage.getItem("BQS_TOKEN");

    const usageState = useSelector((state) => state.user.subscriptionUsage);
    const usageData = usageState.value;
    const isUsageLoading = usageState.isStarted;

    // Initial fetch handled by Nav.jsx globally
    useEffect(() => {
        // We only refetch usage here, not subscriptions
    }, []);

    const primaryPlanId = subscriptionsState.value?.[0]?._id;
    const usagePlanId = usageData?.subscription?._id;
    const lastFetchedPlanIdRef = useRef(null);

    useEffect(() => {
        const subs = subscriptionsState.value;
        
        const enforcement = (process.env.REACT_APP_SUBSCRIPTION_ENFORCEMENT || 'hard').trim().toLowerCase();
        const isEnforced = enforcement === 'hard';

        // Only fetch if:
        // 1. We have a token and subs
        // 2. We haven't successfully fetched this plan's usage yet
        // 3. We aren't already loading
        // 4. We haven't JUST tried this exact plan ID (to prevent loops if backend is slow)
        const shouldFetch = isEnforced && jwtToken && Array.isArray(subs) && subs.length > 0 && 
                          (!usageData || usagePlanId !== primaryPlanId) && 
                          !isUsageLoading && 
                          lastFetchedPlanIdRef.current !== primaryPlanId;

        if (shouldFetch) {
            lastFetchedPlanIdRef.current = primaryPlanId;
            dispatch(fetchSubscriptionUsage(jwtToken, "smartInsights"));
        }
    }, [jwtToken, primaryPlanId, usagePlanId, isUsageLoading, dispatch, usageData]);

    const activePlanInfo = React.useMemo(() => {
        const subs = subscriptionsState.value;
        if (!Array.isArray(subs) || subs.length === 0) return null;

        const activeSub = subs[0];
        const name = usageData?.subscription?.plan?.name || activeSub?.plan?.name || null;
        const insightsFeatures = usageData?.subscription?.plan?.insightsFeatures || activeSub?.plan?.insightsFeatures || {};
        const insightsUsage = usageData?.currentPeriod?.insightsUsage || activeSub?.currentUsage?.insightsUsage || {};

        const remainingUploads = getRemainingQuota(
            insightsFeatures?.maxVideoUploads,
            insightsUsage?.videoUploads ??
            insightsUsage?.usedVideoUploads ??
            insightsUsage?.uploadsUsed ??
            insightsUsage?.videoUploadCount
        );

        const billingPeriodEnd = activeSub?.billingPeriod?.end || usageData?.subscription?.billingPeriod?.end;
        const currentTermEnd = activeSub?.currentTermEnd || usageData?.subscription?.currentTermEnd || activeSub?.expiresAt;
        
        let renewsOnDate = null;
        if (billingPeriodEnd) {
            const date = new Date(billingPeriodEnd);
            if (!isNaN(date.getTime())) {
                renewsOnDate = date.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
            }
        } else if (currentTermEnd) {
            const isSeconds = String(currentTermEnd).length <= 10;
            const date = new Date(isSeconds ? currentTermEnd * 1000 : currentTermEnd);
            if (!isNaN(date.getTime())) {
                renewsOnDate = date.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
            }
        }

        // Harden status detection: only check the primary active sub and usage data
        const primarySub = subs[0];
        const primaryStat = String(primarySub?.status || primarySub?.state || '').trim().toLowerCase();
        const usageStat = String(usageData?.subscription?.status || usageData?.subscription?.state || '').trim().toLowerCase();
        
        const isPrimaryCancelled = !!primarySub?.pendingCancelAt || primaryStat.includes('cancel') || primaryStat === 'non_renewing';
        const isUsageCancelled = !!usageData?.subscription?.pendingCancelAt || usageStat.includes('cancel');
        
        const status = (isPrimaryCancelled || isUsageCancelled) ? 'cancelled' : 'active';
        const isFirstStep = name === "First Step" || activeSub?.plan?.code === "insights-first-step" || usageData?.subscription?.plan?.code === "insights-first-step";

        return {
            name,
            remainingUploads,
            renewsOnDate,
            status,
            isFirstStep,
        };
    }, [subscriptionsState.value, usageData, usageState]);

    const handleOpenUploadVideoGuidelineDialog = () => {
        dispatch(setUploadGuidelineVideoDialogOpen(true));
    };

    const handleOpenMembershipPage = () => {
        navigate('/game-posts/subscription-pricing?manage=1');
        if (isMenuOpen && toggleMenu) toggleMenu();
    };

    const enforcement = (process.env.REACT_APP_SUBSCRIPTION_ENFORCEMENT || 'hard').trim().toLowerCase();
    const isEnforced = enforcement === 'hard';

    const navItems = [
        { to: "game-videos", icon: Video, label: "Videos" },
        { to: "game-reports", icon: FileText, label: "Reports" },
        // Always pass ?manage=1 so the subscription page skips the auto-redirect
        ...(isEnforced ? [{ to: "subscription-pricing?manage=1", icon: Monitor, label: "Membership" }] : []),
        { to: "game-supports", icon: Phone, label: "Support" },
    ];

    return (
        <aside
            className={`
                flex flex-col bg-white shadow-xl z-40 transition-all duration-300 ease-in-out h-full
                lg:sticky lg:top-0 lg:h-screen lg:z-30
                ${isCollapsed ? 'w-20' : 'w-full lg:w-72'}
            `}
        >
            {/* Toggle Button (Desktop) */}
            <button
                onClick={() => setIsCollapsed(!isCollapsed)}
                className="absolute -right-3 top-1/2 -translate-y-1/2 bg-white border border-gray-200 rounded-full p-1 shadow-md hover:bg-gray-50 text-gray-600 z-50 hidden lg:flex"
                style={{ transition: 'background-color 200ms ease, transform 200ms ease' }}
            >
                <span
                    style={{
                        display: 'flex',
                        transition: 'transform 300ms cubic-bezier(0.4, 0, 0.2, 1)',
                        transform: isCollapsed ? 'rotate(0deg)' : 'rotate(180deg)',
                    }}
                >
                    <ChevronRight size={16} />
                </span>
            </button>

            {/* Navigation Links */}
            <div className={`flex-1 overflow-y-auto scrollbar-hide px-3 space-y-2 pb-8 ${isCollapsed ? 'pt-24' : 'pt-8 lg:pt-28'}`}>
                {navItems.map((item) => (
                    <NavLink
                        key={item.to}
                        to={item.to}
                        onClick={toggleMenu}
                        className={({ isActive }) => `
                            sidebar-link
                            relative flex items-center gap-3 px-3 py-3 rounded-xl group transition-all duration-200
                            ${isActive ? 'bg-secondary-color !text-primary-color font-medium shadow-sm' : 'text-gray-600 hover:bg-secondary-color hover:!text-primary-color'}
                            ${isCollapsed ? 'justify-center' : ''}
                        `}
                    >
                        {({ isActive }) => (
                            <>
                                {/* Active Indicator Bar */}
                                <div className={`
                                    absolute left-0 top-1/2 -translate-y-1/2 w-1.5 h-7 bg-primary-color rounded-r-full transition-all duration-300 transform origin-left z-20
                                    ${isActive ? 'opacity-100 scale-y-100' : 'opacity-0 scale-y-0'}
                                `} />

                                <item.icon
                                    size={22}
                                    className={`flex-shrink-0 transition-colors duration-200 relative z-10 ${isActive ? '!text-primary-color' : 'text-gray-500 group-hover:!text-primary-color'}`}
                                />

                                {!isCollapsed && (
                                    <span className="whitespace-nowrap overflow-hidden transition-all duration-300 origin-left relative z-10">
                                        {item.label}
                                    </span>
                                )}
                            </>
                        )}
                    </NavLink>
                ))}
            </div>

            {/* Guidelines Section */}
            {isGameVideoPath && (
                <div className="mx-3 mb-4 flex-shrink-0">
                    <div
                        className="border-t border-gray-100 bg-gray-50/50"
                        style={{
                            opacity: isCollapsed ? 0 : 1,
                            height: isCollapsed ? 0 : 'auto',
                            overflow: 'hidden',
                            padding: isCollapsed ? '0 1rem' : '1rem',
                            transform: isCollapsed ? 'scaleY(0)' : 'scaleY(1)',
                            transformOrigin: 'top',
                            transition: 'transform 350ms ease, opacity 350ms ease, padding 350ms ease',
                            pointerEvents: isCollapsed ? 'none' : 'auto',
                        }}
                    >
                        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                            <h2 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
                                Guidelines
                            </h2>

                            <div className="space-y-4 max-h-64 overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-gray-200">
                                {guidelines.map((ele) => (
                                    <div key={ele.id} className="flex gap-3 items-start">
                                        <Avatar
                                            sx={{
                                                width: 20,
                                                height: 20,
                                                fontSize: '10px',
                                                bgcolor: '#247E45',
                                                flexShrink: 0,
                                                marginTop: '2px'
                                            }}
                                        >
                                            {ele.id}
                                        </Avatar>
                                        <p className="text-xs text-gray-600 leading-relaxed">
                                            {ele.guideline}
                                        </p>
                                    </div>
                                ))}
                            </div>

                            <div className="mt-4 pt-3 border-t border-gray-100 text-center">
                                <button
                                    onClick={handleOpenUploadVideoGuidelineDialog}
                                    className="text-xs text-emerald-600 hover:text-emerald-700 font-medium flex items-center justify-center gap-1 w-full transition-colors"
                                >
                                    Watch guideline video
                                    <ExternalLink size={12} />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Collapsed Guidelines Hint */}
            {isGameVideoPath && (
                <div
                    className="border-t border-gray-100 flex justify-center"
                    style={{
                        opacity: isCollapsed ? 1 : 0,
                        height: isCollapsed ? 'auto' : 0,
                        overflow: 'hidden',
                        padding: isCollapsed ? '1rem' : '0 1rem',
                        transform: isCollapsed ? 'scaleY(1)' : 'scaleY(0)',
                        transformOrigin: 'top',
                        transition: 'transform 350ms ease, opacity 350ms ease, padding 350ms ease',
                        pointerEvents: isCollapsed ? 'auto' : 'none',
                    }}
                >
                    <button
                        onClick={() => setIsCollapsed(false)}
                        className="w-10 h-10 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center hover:bg-emerald-100"
                        title="View Guidelines"
                        style={{ transition: 'background-color 200ms ease' }}
                    >
                        <FileText size={20} />
                    </button>
                </div>
            )}

            {/* Active Plan Badge */}
            {isEnforced && activePlanInfo?.name && !isCollapsed && (
                <div className="mx-3 mb-4 flex-shrink-0">
                    <button
                        type="button"
                        className="w-full rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-3 text-left hover:bg-emerald-100 transition-colors"
                        onClick={handleOpenMembershipPage}
                        title="View your current plan"
                    >
                        <div className="flex items-start gap-2.5">
                            <div className="w-8 h-8 rounded-full bg-[#247E45] flex items-center justify-center flex-shrink-0 mt-0.5">
                                <CreditCard size={14} className="text-white" />
                            </div>

                            <div className="min-w-0 flex-1">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <p className="text-[9px] text-emerald-600 font-semibold uppercase tracking-wider leading-none mb-1">
                                            Current Plan
                                        </p>
                                        <p className="text-xs font-bold text-emerald-900 truncate">
                                            {activePlanInfo.name}
                                        </p>
                                    </div>
                                    <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse flex-shrink-0 mt-1.5" />
                                </div>

                                <div className="mt-3 pt-3 border-t border-emerald-200 space-y-2">
                                    <div className="flex items-center justify-between gap-3 text-[11px]">
                                        <span className="text-emerald-700 font-medium">Uploads left</span>
                                        <span className="text-emerald-950 font-bold">
                                            {formatCountValue(activePlanInfo.remainingUploads)}
                                        </span>
                                    </div>
                                    {(activePlanInfo.renewsOnDate || activePlanInfo.status === 'cancelled' || activePlanInfo.isFirstStep) && (
                                        <div className="flex items-center justify-between gap-3 text-[11px]">
                                            <span className="text-emerald-700 font-medium">
                                                {activePlanInfo.status === 'cancelled' ? 'Status' : activePlanInfo.isFirstStep ? 'Status' : 'Renews on'}
                                            </span>
                                            <span className={`${activePlanInfo.status === 'cancelled' ? 'text-red-500' : 'text-emerald-950'} font-bold`}>
                                                {activePlanInfo.status === 'cancelled' ? 'Cancelled' : activePlanInfo.isFirstStep ? 'Upgrade to Paid Plans' : activePlanInfo.renewsOnDate}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </button>
                </div>
            )}

            {/* Collapsed plan icon hint */}
            {isEnforced && activePlanInfo?.name && isCollapsed && (
                <div className="flex justify-center mb-4 px-3">
                    <NavLink
                        to="subscription-pricing?manage=1"
                        title={`Active: ${activePlanInfo.name}`}
                        className="w-10 h-10 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center hover:bg-emerald-100 transition-colors"
                    >
                        <CreditCard size={18} className="text-[#247E45]" />
                    </NavLink>
                </div>
            )}

            <UploadGuidelineVideoModel />
        </aside>
    );
};

export default GamePostSidebar;
